import React, { useCallback, useEffect } from "react";

import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import Deal from "@mapmycustomers/shared/types/entity/Deal";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import useStateWithDebouncedListener from "@mapmycustomers/shared/util/hook/useStateWithDebouncedListener";
import { SearchField } from "@mapmycustomers/ui";

import useView from "@app/component/view/utils/useView";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import styles from "./QuickNameFilter.module.scss";

interface Props {}

const QuickSearchFilter: React.FC<Props> = () => {
  const intl = useIntl();
  const analyticIssuer = useAnalytics();
  const {
    updateFilterModel,
    viewState: { filter },
  } = useView<Deal>();

  const updateFilters = useCallback(
    (value: string) =>
      updateFilterModel({
        [DealFieldName.QUICK_FILTER]: value
          ? { operator: FilterOperator.CONTAINS, value }
          : undefined,
      }),
    [updateFilterModel]
  );

  const handleClickEvent = useCallback(() => {
    analyticIssuer?.initiated(["Search"]);
  }, [analyticIssuer]);

  const handleEnterPress = useDynamicCallback(() => {
    updateFilters(value);
    analyticIssuer?.completed(["Search", "Enter"]);
  });

  const [value, setValue, resetValue] = useStateWithDebouncedListener([updateFilters, 500], "");
  useEffect(() => {
    if (
      filter[DealFieldName.QUICK_FILTER] &&
      isSimpleCondition(filter[DealFieldName.QUICK_FILTER]) &&
      filter[DealFieldName.QUICK_FILTER].operator === FilterOperator.CONTAINS
    ) {
      resetValue(filter[DealFieldName.QUICK_FILTER].value);
    } else {
      resetValue("");
    }
  }, [filter, resetValue]);

  return (
    <SearchField
      className={styles.container}
      onChange={setValue}
      onClick={handleClickEvent}
      onPressEnter={handleEnterPress}
      placeholder={intl.formatMessage({
        id: "deal.topPanel.search.placeholder",
        defaultMessage: "Search for a deal",
        description: "Deal list search placeholder",
      })}
      size="middle"
      value={value}
    />
  );
};

export default QuickSearchFilter;
